const OPEN_CLASS = 'is-open';
const ITEM_SELECTOR = '.main-navigation-dropdown-item';
const TOGGLE_SELECTOR = '.main-navigation-subitems-toggle';

/**
 * Drives the third-level menu for the Header's "Tertiary Menu Type" setting. Default mode needs no
 * JavaScript at all, and so does the dropdown's hover trigger, which CSS handles on its own. This
 * only runs for the two modes that need a click: accordion, and dropdown set to open on click.
 */
function handleTertiaryMenu(): void {
  const nav = document.querySelector<HTMLElement>('.tertiary-menu-accordion, .tertiary-menu-dropdown.tertiary-menu-trigger-click');
  if (!nav) {
    return;
  }

  // One open at a time in accordion mode, matching the top-level nav's data-bs-parent behaviour.
  const isAccordion = nav.classList.contains('tertiary-menu-accordion');

  /** Opens or closes one parent item and keeps its toggle's aria-expanded in step. */
  const setOpen = (item: HTMLElement, open: boolean): void => {
    item.classList.toggle(OPEN_CLASS, open);
    item.querySelector<HTMLElement>(TOGGLE_SELECTOR)?.setAttribute('aria-expanded', String(open));
  };

  const closeAll = (except?: HTMLElement): void => {
    nav.querySelectorAll<HTMLElement>(`${ITEM_SELECTOR}.${OPEN_CLASS}`).forEach((open) => {
      if (open !== except) {
        setOpen(open, false);
      }
    });
  };

  nav.querySelectorAll<HTMLButtonElement>(TOGGLE_SELECTOR).forEach((toggle) => {
    toggle.addEventListener('click', (event) => {
      // The toggle sits next to a real link, so stop the click reaching the parent item and
      // triggering navigation or the enclosing collapse.
      event.preventDefault();
      event.stopPropagation();

      const item = toggle.closest<HTMLElement>(ITEM_SELECTOR);
      if (!item) {
        return;
      }

      const willOpen = !item.classList.contains(OPEN_CLASS);
      if (willOpen && isAccordion) {
        closeAll(item);
      }
      setOpen(item, willOpen);
    });
  });

  // A flyout that stays open after the pointer leaves the menu looks stuck, so close on an
  // outside click. Accordion mode expands in place and is left alone.
  if (!isAccordion) {
    document.addEventListener('click', (event) => {
      if (!nav.contains(event.target as Node)) {
        closeAll();
      }
    });
  }

  // Escape closes whatever is open and returns focus to the toggle that opened it, so keyboard
  // users are not stranded inside a submenu.
  nav.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' && event.key !== 'Esc') {
      return;
    }
    const open = nav.querySelector<HTMLElement>(`${ITEM_SELECTOR}.${OPEN_CLASS}`);
    if (open) {
      setOpen(open, false);
      open.querySelector<HTMLElement>(TOGGLE_SELECTOR)?.focus();
    }
  });
}

document.addEventListener('DOMContentLoaded', handleTertiaryMenu);
